import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBundleBySlug } from '../lib/api';
import { getPlantImage } from '../data/plantEmojis';
import './BundlePage.css';

/* ── Star Rating ─────────────────────────────────────────────────────── */
function StarRating({ rating, count, size = 12 }) {
  if (!count || count === 0) return <span className="bpc__rating-empty">No reviews yet</span>;
  return (
    <div className="bpc__stars">
      {[1,2,3,4,5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24"
          fill={i <= Math.round(rating) ? '#F59E0B' : 'none'}
          stroke="#F59E0B" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
      <span className="bpc__rating-val">{Number(rating).toFixed(1)}</span>
      <span className="bpc__rating-cnt">| {count}</span>
    </div>
  );
}

/* ── Product Detail Popup ─────────────────────────────────────────────── */
const BULLETS_PREVIEW = 4;

function ProductPopup({ bundleProduct, isSelected, isFull, onToggle, onClose }) {
  const { product, sizeLabel, planterName, colorLabel, variantPrice, variantImage } = bundleProduct;
  const [showAllBullets, setShowAllBullets] = React.useState(false);

  const variantLabel = [sizeLabel, planterName, colorLabel].filter(Boolean).join(' · ');

  // Resolve displayed price from variantPrice (server-resolved) or fall back
  const displayPrice = variantPrice || null;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="bpp-overlay" onClick={onClose}>
      <div className="bpp-modal" onClick={e => e.stopPropagation()}>

        {/* Close */}
        <button className="bpp-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="bpp-body">
          {/* Left: image */}
          <div className="bpp-img-col">
            {(variantImage || product.image)
              ? <img src={variantImage || product.image} alt={product.name} className="bpp-img" />
              : <div className="bpp-img-fallback">🌿</div>
            }
          </div>

          {/* Right: info */}
          <div className="bpp-info-col">
            {/* Name + rating */}
            <div className="bpp-header">
              <h2 className="bpp-name">{product.name}</h2>
              <div className="bpp-rating-row">
                <StarRating rating={product.rating} count={product.reviews} size={14} />
              </div>
            </div>

            {/* Care info pills */}
            {(product.care?.water || product.care?.light) && (
              <div className="bpp-care-box">
                <span className="bpp-care-label">How easy it<br/>is to care?</span>
                {product.care?.water && (
                  <div className="bpp-care-item">
                    <span className="bpp-care-icon">💧</span>
                    <div>
                      <div className="bpp-care-name">Water</div>
                      <div className="bpp-care-val">{product.care.water}</div>
                    </div>
                  </div>
                )}
                {product.care?.light && (
                  <div className="bpp-care-item">
                    <span className="bpp-care-icon">☀️</span>
                    <div>
                      <div className="bpp-care-name">Light</div>
                      <div className="bpp-care-val">{product.care.light}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description — plain text, collapsible */}
            {product.description && (
              <div className="bpp-desc-wrap">
                <p className={`bpp-desc${showAllBullets ? ' bpp-desc--expanded' : ''}`}>
                  {product.description}
                </p>
                {product.description.length > 180 && (
                  <button className="bpp-see-more" onClick={() => setShowAllBullets(v => !v)}>
                    {showAllBullets ? 'See less ▲' : 'See more ▼'}
                  </button>
                )}
              </div>
            )}

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="bpp-tags">
                {product.tags.slice(0, 4).map(t => (
                  <span key={t} className="bpp-tag">{t}</span>
                ))}
              </div>
            )}

            {/* Variant rows — like the product page */}
            {(sizeLabel || planterName || colorLabel) && (
              <div className="bpp-variant-rows">
                {sizeLabel && (
                  <div className="bpp-variant-row">
                    <span className="bpp-variant-axis">{product.variant1Name || 'Size'}:</span>
                    <span className="bpp-variant-pill">{sizeLabel}</span>
                  </div>
                )}
                {planterName && (
                  <div className="bpp-variant-row">
                    <span className="bpp-variant-axis">{product.variant2Name || 'Planter'}:</span>
                    <span className="bpp-variant-pill">{planterName}</span>
                  </div>
                )}
                {colorLabel && (
                  <div className="bpp-variant-row">
                    <span className="bpp-variant-axis">Color:</span>
                    <span className="bpp-variant-pill">{colorLabel}</span>
                  </div>
                )}
              </div>
            )}

            {/* Footer: price + CTA */}
            <div className="bpp-footer">
              {displayPrice != null && (
                <div className="bpp-price-block">
                  <span className="bpp-price">₹{displayPrice.toLocaleString()}</span>
                </div>
              )}

              {isSelected ? (
                <button className="bpp-cta bpp-cta--remove" onClick={() => { onToggle(); onClose(); }}>
                  Remove
                </button>
              ) : isFull ? (
                <button className="bpp-cta bpp-cta--disabled" disabled>
                  Bundle Full
                </button>
              ) : (
                <button className="bpp-cta bpp-cta--add" onClick={() => { onToggle(); onClose(); }}>
                  Add To Bundle
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── BundleProductCard ────────────────────────────────────────────────── */
function BundleProductCard({ bundleProduct, isSelected, onToggle, disabled, onCardClick }) {
  const { product, sizeLabel, planterName, colorLabel, variantImage } = bundleProduct;
  const imgSrc = variantImage || product.image || getPlantImage('');
  const variantLabel = [sizeLabel, planterName, colorLabel].filter(Boolean).join(' · ');

  return (
    <article className={`bpc${isSelected ? ' bpc--selected' : ''}`}>
      {/* Entire image area opens popup */}
      <div className="bpc__img-wrap" onClick={onCardClick}>
        {isSelected && <span className="bpc__selected-badge">SELECTED</span>}
        {!isSelected && product.badge && <span className="bpc__badge">{product.badge}</span>}
        {imgSrc
          ? <img src={imgSrc} alt={product.name} className="bpc__img" loading="lazy" />
          : <div className="bpc__img-fallback">🌿</div>
        }
      </div>

      {/* Info — clicking name also opens popup */}
      <div className="bpc__info" onClick={onCardClick} style={{ cursor: 'pointer' }}>
        <StarRating rating={product.rating} count={product.reviews} />
        <h3 className="bpc__name">{product.name}</h3>
        {variantLabel && <span className="bpc__variant-label">{variantLabel}</span>}
      </div>

      {/* CTA — does NOT open popup, directly toggles */}
      {isSelected ? (
        <button className="bpc__add-btn bpc__add-btn--remove" onClick={onToggle}>
          Remove
        </button>
      ) : disabled ? (
        <button className="bpc__add-btn bpc__add-btn--disabled" disabled>
          Bundle Full
        </button>
      ) : (
        <button className="bpc__add-btn bpc__add-btn--add" onClick={onToggle}>
          Add To Bundle
        </button>
      )}
    </article>
  );
}

/* ── Skeletons ────────────────────────────────────────────────────────── */
function GridSkeletons({ count = 8 }) {
  return (
    <div className="bp__grid">
      {[...Array(count)].map((_, i) => <div key={i} className="bp__skel-card" />)}
    </div>
  );
}

/* ── BundlePage ───────────────────────────────────────────────────────── */
const CART_KEY = 'nq_cart';

function getCartBundleItem(bundleId) {
  try {
    const cart = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    return cart.find(i => i.type === 'bundle' && String(i.bundleId) === String(bundleId)) || null;
  } catch { return null; }
}

export default function BundlePage({ addToCart }) {
  const { slug } = useParams();

  const [bundle,         setBundle]         = useState(null);
  const [bundleProducts, setBundleProducts] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [notFound,       setNotFound]       = useState(false);
  const [selected,       setSelected]       = useState([]);
  const [popup,          setPopup]          = useState(null);
  const [addedMsg,       setAddedMsg]       = useState('');
  const [isInCart,       setIsInCart]       = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getBundleBySlug(slug)
      .then(({ bundle, bundleProducts }) => {
        setBundle(bundle);
        setBundleProducts(bundleProducts || []);

        // Restore selected state from cart on page refresh.
        const cartItem = getCartBundleItem(bundle._id);
        if (cartItem?.bundleSelectedProducts?.length > 0) {
          const restored = cartItem.bundleSelectedProducts
            .map(cp => (bundleProducts || []).find(bp => String(bp.productId) === String(cp._id)))
            .filter(Boolean);
          if (restored.length > 0) {
            setSelected(restored);
            setIsInCart(true);
          }
        }
      })
      .catch(err => {
        if (err.message?.includes('404') || err.message?.includes('not found')) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const maxItems = bundle?.maxItems || 4;
  const isFull   = selected.length >= maxItems;

  const toggleProduct = useCallback((bp) => {
    const pid = bp.productId;
    setSelected(prev => {
      const already = prev.find(s => s.productId === pid);
      if (already) return prev.filter(s => s.productId !== pid);
      if (prev.length >= maxItems) return prev;
      return [...prev, bp];
    });
  }, [maxItems]);

  const handleAddToCart = () => {
    if (!bundle || selected.length === 0 || !addToCart) return;
    const cartPayload = {
      type:       'bundle',
      bundleId:   bundle._id,
      bundleSlug: bundle.slug,
      name:       bundle.name,
      price:      bundle.price,
      qty:        1,
      image:      bundle.image || '',
      imageUrl:   bundle.image || '',
      bundleSelectedProducts: selected.map(bp => ({
        _id:         bp.productId,
        name:        bp.product?.name    || '',
        imageUrl:    bp.variantImage || bp.product?.image || '',
        sizeLabel:   bp.sizeLabel        || '',
        planterName: bp.planterName      || '',
        colorLabel:  bp.colorLabel       || '',
      })),
      bundleMaxItems: maxItems,
      slug: null,
    };

    if (isInCart) {
      // Update bundleSelectedProducts in place — do not increment qty.
      try {
        const cartItemId = `bundle||${bundle._id}`;
        const cart = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
        const updatedCart = cart.map(i =>
          i.cartItemId === cartItemId
            ? { ...i, bundleSelectedProducts: cartPayload.bundleSelectedProducts }
            : i
        );
        localStorage.setItem(CART_KEY, JSON.stringify(updatedCart));
        window.dispatchEvent(new StorageEvent('storage', { key: CART_KEY }));
      } catch (_) { /* ignore */ }
      setAddedMsg('Bundle updated!');
    } else {
      addToCart(cartPayload, 1);
      setIsInCart(true);
      setAddedMsg('Added to cart!');
    }
    setTimeout(() => setAddedMsg(''), 2000);
  };

  if (!loading && notFound) {
    return (
      <main>
        <div className="bp__not-found">
          <h1>Bundle not found</h1>
          <p>This bundle may no longer be available.</p>
          <Link to="/collections/plants">Browse All Plants →</Link>
        </div>
      </main>
    );
  }

  const remaining = maxItems - selected.length;

  return (
    <main>
      {/* ── Hero ── */}
      <div className="bp__hero">
        {bundle?.image && <img src={bundle.image} alt={bundle?.name} className="bp__hero-bg" />}
        <div className="bp__hero-overlay">
          <div className="bp__hero-tag">🌿 Bundle Deal</div>
          <h1 className="bp__hero-title">{bundle?.name || ' '}</h1>
          {bundle?.description && <p className="bp__hero-desc">{bundle.description}</p>}
          <div className="bp__hero-meta">
            {bundle && <div className="bp__hero-price">₹{bundle.price.toLocaleString()}</div>}
            {bundle && <div className="bp__hero-pill">Pick any {bundle.maxItems} plants</div>}
          </div>
        </div>
      </div>

      {/* ── Product Grid ── */}
      <div className="bp__content">
        {loading ? (
          <GridSkeletons count={8} />
        ) : bundleProducts.length === 0 ? (
          <div className="bp__empty">
            <span className="bp__empty-icon">🌱</span>
            <h3 className="bp__empty-title">No plants available in this bundle</h3>
            <p className="bp__empty-body">Check back soon!</p>
          </div>
        ) : (
          <div className="bp__grid">
            {bundleProducts.map(bp => {
              const isSelected = !!selected.find(s => s.productId === bp.productId);
              const isDisabled = !isSelected && isFull;
              return (
                <BundleProductCard
                  key={bp.productId}
                  bundleProduct={bp}
                  isSelected={isSelected}
                  disabled={isDisabled}
                  onToggle={() => toggleProduct(bp)}
                  onCardClick={() => setPopup(bp)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Product Detail Popup ── */}
      {popup && (
        <ProductPopup
          bundleProduct={popup}
          isSelected={!!selected.find(s => s.productId === popup.productId)}
          isFull={isFull && !selected.find(s => s.productId === popup.productId)}
          onToggle={() => toggleProduct(popup)}
          onClose={() => setPopup(null)}
        />
      )}

      {/* ── Fixed bottom bar ── */}
      <div className={`bp__bottom-bar${selected.length > 0 ? ' bp__bottom-bar--visible' : ''}`}>
        <div className="bp__bottom-bar__inner">
          <div className="bp__bottom-slots">
            {selected.map(bp => (
              <div key={bp.productId} className="bp__slot bp__slot--filled">
                {(bp.variantImage || bp.product?.image)
                  ? <img src={bp.variantImage || bp.product.image} alt={bp.product.name} />
                  : <span>🌿</span>
                }
                <button
                  className="bp__slot__remove"
                  onClick={() => setSelected(prev => prev.filter(s => s.productId !== bp.productId))}
                  aria-label={`Remove ${bp.product?.name}`}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {Array.from({ length: Math.max(0, maxItems - selected.length) }).map((_, i) => (
              <div key={`empty-${i}`} className="bp__slot bp__slot--empty" />
            ))}
          </div>

          <div className="bp__bottom-info">
            <span className="bp__bottom-title">Buy any {maxItems} at ₹{bundle?.price?.toLocaleString()}</span>
            <span className="bp__bottom-subtitle">
              {remaining > 0 ? `Add ${remaining} more product${remaining > 1 ? 's' : ''} in the Box` : 'Bundle complete!'}
            </span>
          </div>

          <button
            className={`bp__bottom-cta${selected.length < maxItems ? ' bp__bottom-cta--inactive' : ''}`}
            onClick={handleAddToCart}
            disabled={selected.length === 0}
          >
            {addedMsg ? (addedMsg.includes('updated') ? '✓ Updated!' : '✓ Added!') : isInCart ? 'UPDATE BUNDLE' : 'BUY NOW'}
          </button>
        </div>
      </div>
    </main>
  );
}